import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Nav, Row, Tab } from "react-bootstrap";
import { Controller, Resolver, useForm } from "react-hook-form";
import { IInstitution } from "../../../shared/interfaces/institution/IInstitution";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import BreadcrumbItem from "../../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../../shared/functions/handleError";
import { InstitutionService } from "../service";
import UserList from "../../users/components/List";
import { IUser } from "../../../shared/interfaces/user/IUser";
import { UserService } from "../../users/service";
import { usePagination } from "../../../shared/hooks/usePagination";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";
import ModalUser from "./ModalUser";
import { IIncident } from "../../../shared/interfaces/incident/IIncident";
import { IncidentService } from "../../incidents/service";
import IncidentList from "../../incidents/components/List";
import { IBeacon } from "../../../shared/interfaces/beacon/IBeacon";
import { BeaconService } from "../../beacons/service";
import BeaconList from "../../beacons/components/List";
import GoogleMapsAutocomplete from "../../../shared/components/GoogleMapsAutocomplete";
import GoogleMapComponent from "../../../shared/components/GoogleMapComponent";

const institutionSchema = yup.object().shape({
  id: yup.number().optional(),
  name: yup.string().required("validation.required").min(2).max(100).trim(),
  email: yup.string().email("validation.emailInvalid").trim().lowercase().nullable().optional(),
  phone: yup
    .string()
    .nullable()
    .optional()
    .test('phone-format', 'validation.phoneInvalid', (value) => {
      if (!value || value === '') return true;
      return /^[\d\s\-+()]+$/.test(value);
    })
    .max(20),
  address: yup.string().required("validation.required").min(5).max(200).trim(),
  city: yup.string().required("validation.required").min(2).max(50).trim(),
  latitude: yup
    .number()
    .required("validation.required")
    .min(-90, "validation.latitudeRange")
    .max(90, "validation.latitudeRange")
    .typeError("validation.latitudeInvalid"),
  longitude: yup
    .number()
    .required("validation.required")
    .min(-180, "validation.longitudeRange")
    .max(180, "validation.longitudeRange")
    .typeError("validation.longitudeInvalid"),
  radius: yup
    .number()
    .nullable()
    .optional()
    .transform((value, originalValue) => {
      if (originalValue === null || originalValue === undefined || originalValue === '') {
        return 100;
      }
      return value;
    })
    .min(10, "validation.radiusRange")
    .max(500, "validation.radiusRange")
    .default(100),
  logo: yup.string().url("validation.urlInvalid").nullable().optional(),
});

const initialValues = {
  name: "",
  address: "",
  city: "",
  latitude: "",
  longitude: "",
  radius: 100,
};

const InstitutionForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { institutionId } = useParams();
  const [users, setUsers] = useState<IUser[]>([]);
  const [incidents, setIncidents] = useState<IIncident[]>([]);
  const [beacons, setBeacons] = useState<IBeacon[]>([]);
  const [isFormDataLoaded, setIsFormDataLoaded] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageKey, setImageKey] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleCloseUserModal = () => setShowUserModal(false);
  const handleShowUserModal = () => {
    setShowUserModal(true);
  };

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<IInstitution>({
    resolver: yupResolver(
      institutionSchema,
    ) as unknown as Resolver<IInstitution>,
    defaultValues: async () => {
      if (Boolean(institutionId)) {
        try {
          const response = await InstitutionService.getSingle(
            Number(institutionId),
          );
          setIsFormDataLoaded(true);
          return {
            ...response?.data,
          };
        } catch (e) {
          handleError(e);
          setIsFormDataLoaded(true);
          return initialValues;
        }
      }
      setIsFormDataLoaded(true);
      return initialValues;
    },
  });

  const institutionName = watch("name");
  const {
    pagination,
    setPagination,
    changeFilterHandler,
    handleUpdateList,
    totalPages,
    setTotalPages,
  } = usePagination({
    perPage: 10,
    paginationKeys: {
      institutionName: "",
    },
  });

  // Separate pagination for incidents
  const {
    pagination: incidentPagination,
    changeFilterHandler: changeIncidentFilterHandler,
    handleUpdateList: handleUpdateIncidentList,
    totalPages: incidentTotalPages,
    setTotalPages: setIncidentTotalPages,
  } = usePagination({
    perPage: 10,
  });

  // Separate pagination for beacons
  const {
    pagination: beaconPagination,
    changeFilterHandler: changeBeaconFilterHandler,
    handleUpdateList: handleUpdateBeaconList,
    totalPages: beaconTotalPages,
    setTotalPages: setBeaconTotalPages,
  } = usePagination({
    perPage: 10,
  });

  const onSubmit = async (data: IInstitution) => {
    console.log('=== Form Submit Called ===');
    console.log('Form Data:', data);
    console.log('Form Errors:', errors);
    console.log('Selected File:', selectedFile);

    const isUpdate = !!data?.id;

    try {
      if (isUpdate) {
        // Update institution data with JSON only
        await InstitutionService.update(data, data?.id);

        // Upload image separately if a new file was selected
        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          await InstitutionService.uploadImage(data.id!, formData);
        }
      } else {
        // Create institution with FormData (includes file)
        await InstitutionService.create(data, data?.id, selectedFile);
      }

      navigate(-1);
      reset();
      setSelectedFile(null);

      enqueueSnackbar(
        isUpdate
          ? t("toasts.successfullyUpdated", {
            name: data?.name,
          })
          : t("toasts.successfullyCreated", {
            name: data?.name,
          }),
        {
          variant: "success",
        },
      );
    } catch (error) {
      handleError(error);
    }
  };

  // Log validation errors whenever they change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('=== Validation Errors ===', errors);
    }
  }, [errors]);

  useEffect(() => {
    if (!isFormDataLoaded) return;

    setPagination((prev: IPagination) => ({
      ...prev,
      institutionName: institutionName || "",
      page: 0,
    }));
  }, [institutionName, setPagination, isFormDataLoaded]);

  useEffect(() => {
    if (!isFormDataLoaded) {
      return;
    }

    if (!institutionName) {
      setUsers([]);
      return;
    }

    if (!pagination.institutionName) {
      return;
    }

    UserService.getAll(pagination)
      .then((response) => {
        setUsers(response?.data || []);
        setTotalPages(response?.totalPages || 0);
      })
      .catch((error) => {
        handleError(error);
        setUsers([]);
      });
  }, [pagination, setTotalPages, isFormDataLoaded, institutionName]);

  // Fetch incidents for this institution
  useEffect(() => {
    if (!isFormDataLoaded || !institutionId) {
      return;
    }

    IncidentService.getAll({ ...incidentPagination, institutionId: Number(institutionId) })
      .then((response) => {
        setIncidents(response?.data || []);
        setIncidentTotalPages(response?.totalPages || 0);
      })
      .catch((error) => {
        handleError(error);
        setIncidents([]);
      });
  }, [incidentPagination, setIncidentTotalPages, isFormDataLoaded, institutionId]);

  // Fetch beacons for this institution
  useEffect(() => {
    if (!isFormDataLoaded || !institutionId) {
      return;
    }

    BeaconService.getAll({ ...beaconPagination, institutionId: Number(institutionId) })
      .then((response) => {
        setBeacons(response?.data || []);
        setBeaconTotalPages(response?.totalPages || 0);
      })
      .catch((error) => {
        handleError(error);
        setBeacons([]);
      });
  }, [beaconPagination, setBeaconTotalPages, isFormDataLoaded, institutionId]);

  // Fetch institution image
  useEffect(() => {
    if (!institutionId) return;

    // Cleanup previous blob URL to prevent memory leaks
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }

    InstitutionService.getImageBlob(Number(institutionId), 200, 200)
      .then((blobUrl) => {
        setImageUrl(blobUrl);
        setImageError(false);
      })
      .catch((err) => {
        console.error("Failed to load institution image:", err);
        setImageUrl(null);
        setImageError(true);
      });

    // Cleanup on unmount
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [institutionId, imageKey]);

  // Handle file selection
  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    if (imageUrl && !institutionId) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(previewUrl);
    setImageError(false);
  };

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("institution.institutions")}
        subTitle={
          institutionId
            ? t("institution.updateInstitution")
            : t("institution.createInstitution")
        }
      />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col md={5}>
            <Card>
              <Card.Body>
                <Row>
                  <Col
                    md={4}
                    className={"d-flex flex-column justify-content-around"}
                  >
                    <div className="position-relative mb-3">
                      {imageUrl && !imageError ? (
                        <img
                          src={imageUrl}
                          alt={watch("name") || t("institution.defaultImageAlt")}
                          className="w-100 card-gallery rounded"
                          style={{ objectFit: 'cover', height: '150px' }}
                          onError={() => {
                            setImageError(true);
                            setImageUrl(null);
                          }}
                        />
                      ) : (
                        <div className="w-100 card-gallery rounded d-flex align-items-center justify-content-center" style={{ height: '150px', backgroundColor: '#f0e7ff', position: 'relative', overflow: 'hidden' }}>
                          {/* Decorative background elements */}
                          <div style={{ position: 'absolute', opacity: 0.1, fontSize: '8rem' }}>
                            <i className="ph-duotone ph-building text-info"></i>
                          </div>
                          {/* Main icon */}
                          <div style={{ position: 'relative', zIndex: 1 }}>
                            <i className="ph-duotone ph-bank text-info" style={{ fontSize: '4rem' }}></i>
                          </div>
                        </div>
                      )}

                      <input
                        type="file"
                        id="institution-image-upload"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          handleFileSelect(file);
                        }}
                      />
                      <label
                        htmlFor="institution-image-upload"
                        className="position-absolute bottom-0 end-0 m-2 btn btn-sm btn-primary"
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ph-duotone ph-pencil-simple"></i>
                      </label>
                    </div>

                    <Button
                      type={"submit"}
                      variant={"primary"}
                      className={"w-100"}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t("button.submitting") : t("button.save")}
                    </Button>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>{t("institution.name")}</Form.Label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder={t("institution.name")}
                            isInvalid={!!errors.name?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name?.message &&
                          t(errors.name.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="phone">
                      <Form.Label>{t("general.phone")}</Form.Label>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="tel"
                            placeholder={t("general.phone")}
                            isInvalid={!!errors.phone?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.phone?.message &&
                          t(errors.phone.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>{t("general.email")}</Form.Label>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="email"
                            placeholder={t("general.email")}
                            isInvalid={!!errors.email?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email?.message &&
                          t(errors.email.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col md={7}>
            <Card>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="address">
                      <Form.Label>{t("general.address")}</Form.Label>
                      <Controller
                        name="address"
                        control={control}
                        render={({ field }) => (
                          <GoogleMapsAutocomplete
                            value={field.value}
                            onChange={field.onChange}
                            onPlaceSelected={(place) => {
                              field.onChange(place.address);
                              setValue("city", place.city);
                              setValue("latitude", place.latitude.toString());
                              setValue("longitude", place.longitude.toString());
                            }}
                            placeholder={t("general.address")}
                            isInvalid={!!errors.address?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address?.message &&
                          t(errors.address.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="city">
                      <Form.Label>{t("general.city")}</Form.Label>
                      <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder={t("general.city")}
                            isInvalid={!!errors.city?.message}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.city?.message &&
                          t(errors.city.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="latitude">
                          <Form.Label>{t("general.latitude")}</Form.Label>
                          <Controller
                            name="latitude"
                            control={control}
                            render={({ field }) => (
                              <Form.Control
                                {...field}
                                type="text"
                                placeholder={t("general.latitude")}
                                isInvalid={!!errors.latitude?.message}
                              />
                            )}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.latitude?.message &&
                              t(errors.latitude.message as string)}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="longitude">
                          <Form.Label>{t("general.longitude")}</Form.Label>
                          <Controller
                            name="longitude"
                            control={control}
                            render={({ field }) => (
                              <Form.Control
                                {...field}
                                type="text"
                                placeholder={t("general.longitude")}
                                isInvalid={!!errors.longitude?.message}
                              />
                            )}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.longitude?.message &&
                              t(errors.longitude.message as string)}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group>
                      <Form.Label>
                        {t("general.radius")}: <strong>{watch("radius") || 100}m</strong>
                      </Form.Label>
                      <Controller
                        name="radius"
                        control={control}
                        render={({ field }) => (
                          <Form.Range
                            {...field}
                            min={10}
                            max={500}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <div className="d-flex justify-content-between mt-1 small text-muted">
                        <span>10m</span>
                        <span>500m</span>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <p className="text-muted small mb-2">
                      <i className="ti ti-info-circle me-1"></i>
                      {t("institution.clickMapToPin")}
                    </p>
                    <GoogleMapComponent
                      latitude={Number(watch("latitude")) || 0}
                      longitude={Number(watch("longitude")) || 0}
                      height="300px"
                      radius={Number(watch("radius")) || 100}
                      onLocationSelect={(lat, lng, address, city) => {
                        setValue("latitude", lat.toString(), { shouldDirty: true });
                        setValue("longitude", lng.toString(), { shouldDirty: true });
                        if (address) {
                          setValue("address", address, { shouldDirty: true });
                        }
                        if (city) {
                          setValue("city", city, { shouldDirty: true });
                        }
                      }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col md={12}>
            <Card>
              <Card.Body>
                <Tab.Container defaultActiveKey="link-0">
                  <Nav variant="underline">
                    <Nav.Item>
                      <Nav.Link eventKey="link-0">{t("user.users")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="link-1">
                        {t("protocol.protocols")}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="link-2">
                        {t("beacon.beacons")}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="link-3">
                        {t("incident.incidents")}
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <Tab.Content className="mt-3">
                    <Tab.Pane eventKey="link-0">
                      <div className={"d-flex justify-content-between mb-3"}>
                        <Form.Control
                          placeholder={t("button.search")}
                          name={"firstName"}
                          value={pagination?.firstName ?? ""}
                          onChange={(e) =>
                            changeFilterHandler(e.target.name, e.target.value)
                          }
                          className={"w-75"}
                        />
                        <Button
                          variant={"primary"}
                          type={"button"}
                          onClick={handleShowUserModal}
                        >
                          {t("button.addNew")}
                        </Button>
                      </div>
                      {!!users?.length ? (
                        <UserList
                          users={users}
                          pagination={pagination}
                          totalPages={totalPages}
                          handleUpdateList={handleUpdateList}
                          changeFilterHandler={changeFilterHandler}
                        />
                      ) : (
                        <div className="text-center py-5">
                          <i className="ph-duotone ph-users" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                          <p className="text-muted mt-3 mb-1">{t("user.noUsers", { defaultValue: "No users found" })}</p>
                          <small className="text-muted">{t("user.addUsersToInstitution", { defaultValue: "Add users to this institution using the button above" })}</small>
                        </div>
                      )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="link-1">
                      <div className="text-center py-5">
                        <i className="ph-duotone ph-file-text" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                        <p className="text-muted mt-3 mb-1">{t("protocol.noProtocols", { defaultValue: "No protocols available" })}</p>
                        <small className="text-muted">{t("protocol.protocolsComingSoon", { defaultValue: "Protocol management coming soon" })}</small>
                      </div>
                    </Tab.Pane>

                    <Tab.Pane eventKey="link-2">
                      {!!beacons?.length ? (
                        <BeaconList
                          beacons={beacons}
                          pagination={beaconPagination}
                          totalPages={beaconTotalPages}
                          handleUpdateList={handleUpdateBeaconList}
                          changeFilterHandler={changeBeaconFilterHandler}

                        />
                      ) : (
                        <div className="text-center py-5">
                          <i className="ph-duotone ph-broadcast" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                          <p className="text-muted mt-3 mb-1">{t("beacon.noBeacons", { defaultValue: "No beacons found" })}</p>
                          <small className="text-muted">{t("beacon.noBeaconsForInstitution", { defaultValue: "This institution doesn't have any beacons yet" })}</small>
                        </div>
                      )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="link-3">
                      {!!incidents?.length ? (
                        <IncidentList
                          incidents={incidents}
                          pagination={incidentPagination}
                          totalPages={incidentTotalPages}
                          handleUpdateList={handleUpdateIncidentList}
                          changeFilterHandler={changeIncidentFilterHandler}
                          showActions={false}
                        />
                      ) : (
                        <div className="text-center py-5">
                          <i className="ph-duotone ph-warning" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                          <p className="text-muted mt-3 mb-1">{t("incident.noIncidents", { defaultValue: "No incidents found" })}</p>
                          <small className="text-muted">{t("incident.noIncidentsForInstitution", { defaultValue: "This institution doesn't have any incidents" })}</small>
                        </div>
                      )}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
      <ModalUser
        showUserModal={showUserModal}
        handleCloseUserModal={handleCloseUserModal}
      />
    </React.Fragment>
  );
};

export default InstitutionForm;
